import {
  Form,
  Input,
  Checkbox,
  Link,
  Button,
  Space,
} from '@arco-design/web-react';
import { FormInstance } from '@arco-design/web-react/es/Form';
import { IconLock, IconUser } from '@arco-design/web-react/icon';
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import useStorage from '@/utils/useStorage';
import useLocale from '@/utils/useLocale';
import locale from './locale';
import styles from './style/index.module.less';

export default function LoginForm() {
  const formRef = useRef<FormInstance>();
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [loginParams, setLoginParams, removeLoginParams] =
    useStorage('loginParams');

  const t = useLocale(locale);

  const [rememberPassword, setRememberPassword] = useState(!!loginParams);

  // 清除登录进度标记
  useEffect(() => {
    sessionStorage.removeItem('login_in_progress');
  }, []);

  function afterLoginSuccess(params, userData) {
    // 记住密码
    if (rememberPassword) {
      setLoginParams(JSON.stringify(params));
    } else {
      removeLoginParams();
    }

    // 处理会员信息
    const memberData = userData.member;

    // 确保avatarUrl字段映射到avatar
    if (memberData.avatarUrl && !memberData.avatar) {
      memberData.avatar = memberData.avatarUrl;
    }

    // 保存token和用户信息
    localStorage.setItem('token', userData.access_token);
    localStorage.setItem('userInfo', JSON.stringify(memberData));

    // 记录登录状态
    localStorage.setItem('userStatus', 'login');

    // 清除登录进度标记
    sessionStorage.removeItem('login_in_progress');

    // 获取重定向URL
    const rawRedirect = new URLSearchParams(window.location.search).get(
      'redirect'
    );

    if (rawRedirect) {
      try {
        // 处理带查询参数的URL
        let redirectUrl = decodeURIComponent(rawRedirect);

        // 检查是否包含多次编码的redirect参数
        if (redirectUrl.includes('redirect=')) {
          // 提取嵌套在redirect参数中的真实目标URL
          const nestedMatch = redirectUrl.match(/redirect=([^&]+)/);
          if (nestedMatch && nestedMatch[1]) {
            redirectUrl = decodeURIComponent(nestedMatch[1]);
          }
        }

        // 确保URL是以/开头的相对路径
        const url = redirectUrl.startsWith('/')
          ? redirectUrl
          : `/${redirectUrl}`;
        console.log('重定向到:', url);
        window.location.href = url;
      } catch (e) {
        console.error('重定向URL解析失败:', e);
        window.location.href = '/';
      }
    } else {
      window.location.href = '/';
    }
  }

  function login(params) {
    setErrorMessages([]);
    setLoading(true);

    // 转换字段名称，从userName改为username
    const apiParams = {
      username: params.username,
      password: params.password,
      platform: 'web',
    };

    axios
      .post('/api/auth/login', apiParams)
      .then((res) => {
        if (res.status === 201) {
          afterLoginSuccess(params, res.data);
        } else {
          setErrorMessages([t['login.form.login.errMsg']]);
        }
      })
      .catch((error) => {
        console.error('登录失败:', error);
        if (
          error.response?.data?.message &&
          Array.isArray(error.response.data.message)
        ) {
          // 处理多个错误信息
          setErrorMessages(error.response.data.message);
        } else {
          // 单个错误信息或通用错误
          setErrorMessages([
            error.response?.data?.message || t['login.form.login.errMsg'],
          ]);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function onSubmitClick() {
    formRef.current.validate().then((values) => {
      login(values);
    });
  }

  // 读取 localStorage，设置初始值
  useEffect(() => {
    const rememberPassword = !!loginParams;
    setRememberPassword(rememberPassword);
    if (formRef.current && rememberPassword) {
      const parseParams = JSON.parse(loginParams);
      formRef.current.setFieldsValue(parseParams);
    }
  }, [loginParams]);

  function handleTokenError() {
    // 清除所有登录信息
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('userStatus');

    // 重定向到登录页
    window.location.href = '/login';
  }

  return (
    <div className={styles['login-form-wrapper']}>
      <div className={styles['login-form-title']}>{t['login.form.title']}</div>
      <div className={styles['login-form-sub-title']}>欢迎回来！</div>
      {errorMessages.length > 0 && (
        <div className={styles['login-form-error-msg']}>
          {errorMessages.map((msg, index) => (
            <div key={index}>{msg}</div>
          ))}
        </div>
      )}
      <Form
        className={styles['login-form']}
        layout="vertical"
        ref={formRef}
        initialValues={{}}
        autoComplete="off"
        style={{ marginTop: 20 }}
      >
        <Form.Item
          field="username"
          style={{ marginBottom: 24 }}
          rules={[
            { required: true, message: t['login.form.userName.errMsg'] },
            {
              validator: (value, callback) => {
                if (value && (value.length < 3 || value.length > 50)) {
                  return callback('用户名长度必须在3到50个字符之间');
                }
                callback();
              },
            },
          ]}
        >
          <Input
            prefix={<IconUser />}
            placeholder={t['login.form.userName.placeholder']}
            onPressEnter={onSubmitClick}
          />
        </Form.Item>
        <Form.Item
          field="password"
          style={{ marginBottom: 24 }}
          rules={[
            { required: true, message: t['login.form.password.errMsg'] },
            {
              validator: (value, callback) => {
                if (value && (value.length < 6 || value.length > 100)) {
                  return callback('密码长度必须在6到100个字符之间');
                }
                callback();
              },
            },
          ]}
        >
          <Input.Password
            prefix={<IconLock />}
            placeholder={t['login.form.password.placeholder']}
            onPressEnter={onSubmitClick}
          />
        </Form.Item>
        <Space size={24} direction="vertical" style={{ width: '100%' }}>
          <div className={styles['login-form-password-actions']}>
            <Checkbox checked={rememberPassword} onChange={setRememberPassword}>
              {t['login.form.rememberPassword']}
            </Checkbox>
            {/* <Link>{t['login.form.forgetPassword']}</Link> */}
          </div>
          <Button type="primary" long onClick={onSubmitClick} loading={loading}>
            {t['login.form.login']}
          </Button>
          {/* <Button
            type="text"
            long
            className={styles['login-form-register-btn']}
          >
            {t['login.form.register']}
          </Button> */}
        </Space>
      </Form>
      <div className={styles['login-form-invitation-notice']}>
        <div className={styles['login-form-invitation-notice-content']}>
          <div className={styles['login-form-invitation-notice-title']}>
            🎯 我们目前采用邀请制
          </div>
          <div className={styles['login-form-invitation-notice-text']}>
            如需体验产品，请<a href="mailto:hi@suwendata.com">联系我们</a>
            获取邀请码
          </div>
        </div>
      </div>
    </div>
  );
}
